import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionAPI } from '../lib/api';
import AlertDialog from '../components/AlertDialog';

const InvestorActivityFee = () => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [paid, setPaid] = useState(false);
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    useEffect(() => {
        checkStatus();
    }, []);

    const checkStatus = async () => {
        setLoading(true);
        try {
            const data = await subscriptionAPI.getInvestorActivityStatus();
            setPaid(data.paid === true);
        } catch (error) {
            setAlert({ isOpen: true, title: 'Error', message: error.message || 'Failed to load status', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handlePay = async () => {
        setPaying(true);
        try {
            await subscriptionAPI.payInvestorActivityFee();
            setAlert({
                isOpen: true,
                title: 'Success',
                message: 'Activity fee recorded. You can now add documents and link to startups.',
                type: 'success'
            });
            setPaid(true);
            setTimeout(() => navigate(`/${tenantId}/investor/dashboard`), 1500);
        } catch (error) {
            setAlert({
                isOpen: true,
                title: 'Error',
                message: error.response?.data?.message || error.message || 'Payment failed',
                type: 'error'
            });
        } finally {
            setPaying(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-lg mx-auto">
            <button
                onClick={() => navigate(`/${tenantId}/investor/dashboard`)}
                className="text-blue-600 hover:text-blue-800 mb-6 flex items-center"
            >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back to Dashboard
            </button>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Investor Activity Fee</h1>
                <p className="text-gray-600 mb-6">
                    To add documents or be linked to startups, pay a one-time fee of <strong>₹1,000</strong>.
                </p>

                {paid ? (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 font-medium">You have already paid the activity fee.</p>
                        <p className="text-green-600 text-sm mt-1">You can add documents and link to startups.</p>
                        <button
                            onClick={() => navigate(`/${tenantId}/investor/dashboard`)}
                            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="text-3xl font-bold text-blue-600 mb-6">₹1,000</div>
                        <p className="text-sm text-gray-500 mb-6">
                            One-time payment. After this you can upload documents and be linked to startups by admins.
                        </p>
                        <button
                            onClick={handlePay}
                            disabled={paying}
                            className="w-full py-3 px-6 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                        >
                            {paying ? 'Processing...' : 'Pay ₹1,000'}
                        </button>
                    </>
                )}
            </div>

            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />
        </div>
    );
};

export default InvestorActivityFee;
